import { LitElement, css, html } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { i18n } from '../i18n/nl';

/**
 * `<embrace-searching-banner>` — footer-banner die de composer vervangt
 * tijdens het venster tussen intake-submit en operator-attach
 * (assistant.step==='searching' of session.phase==='requesting'/'queued').
 *
 * Layout matcht `<embrace-ended-banner>` (zelfde sunken-bg + border-top)
 * zodat de footer niet van hoogte springt bij stage-overgangen.
 *
 * Status-tekst rouleert op 10s/30s-drempels zodat de wacht "leeft"; de
 * avatar-strip-animatie loopt puur op CSS-keyframes.
 */
@customElement('embrace-searching-banner')
export class EmbraceSearchingBanner extends LitElement {
  // Plain field i.p.v. @state: een Lit re-render zou per tick de hele
  // avatar-strip (14 spans) opnieuw evalueren puur om de timer-tekst bij
  // te werken. We muteren timer- en status-tekst in plaats daarvan
  // rechtstreeks op de DOM via @query-refs.
  private elapsedSec = 0;
  private tickHandle: number | null = null;

  @query('.timer-text') private timerEl?: HTMLElement;
  @query('.status') private statusEl?: HTMLElement;

  override connectedCallback(): void {
    super.connectedCallback();
    this.tickHandle = window.setInterval(() => {
      this.elapsedSec += 1;
      if (this.timerEl) this.timerEl.textContent = this.formatElapsed();
      if (this.statusEl) this.statusEl.textContent = this.statusText();
    }, 1000);
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    if (this.tickHandle !== null) {
      clearInterval(this.tickHandle);
      this.tickHandle = null;
    }
  }

  static override styles = css`
    :host {
      display: block;
      /* .track heeft width:max-content; min-width:0 stopt die intrinsieke
         breedte zodat slot-/grid-parents niet meeuitrekken. */
      min-width: 0;
    }

    .banner {
      padding: 12px 16px;
      background: var(--ec-surface-sunken);
      border-top: 1px solid var(--ec-border);
      display: flex;
      flex-direction: column;
      gap: 8px;
      min-width: 0;
    }

    .head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
    }
    .status {
      font: 500 13px/1.35 var(--ec-font-family);
      color: var(--ec-text);
    }
    .timer {
      display: flex;
      align-items: center;
      gap: 4px;
      font: 500 11.5px/1 var(--ec-font-family);
      color: #5E5F62;
      font-variant-numeric: tabular-nums;
      flex-shrink: 0;
    }
    .pulse-dot {
      width: 6px;
      height: 6px;
      border-radius: 9999px;
      background: var(--ec-primary);
      animation: pulse 1.4s ease-in-out infinite;
    }
    @keyframes pulse {
      0%, 100% { opacity: 0.35; transform: scale(.8); }
      50% { opacity: 1; transform: scale(1); }
    }
    @media (prefers-reduced-motion: reduce) {
      .pulse-dot { animation: none; opacity: 1; }
    }

    /* ---- Avatar-strip ---- */

    .strip {
      position: relative;
      overflow: hidden;
      min-width: 0;
      /* Genoeg ruimte voor de emoji-bubbel boven de cirkel + de cirkel zelf. */
      height: 48px;
      /* Verloop aan de rand zodat avatars uit/in-faden i.p.v. kakelhard
         tegen de banner-rand aan tikken. */
      mask-image: linear-gradient(
        to right,
        transparent 0,
        #000 16px,
        #000 calc(100% - 16px),
        transparent 100%
      );
      -webkit-mask-image: linear-gradient(
        to right,
        transparent 0,
        #000 16px,
        #000 calc(100% - 16px),
        transparent 100%
      );
    }

    .track {
      display: flex;
      gap: 14px;
      padding: 8px 8px 4px;
      width: max-content;
      animation: slide 18s linear infinite;
    }

    /* De track bevat 2× dezelfde avatar-set zodat translateX(-50%) een
       seamless loop oplevert: zodra we 50% verschoven zijn lopen we
       door op een visueel identiek punt. */
    @keyframes slide {
      from { transform: translateX(0); }
      to   { transform: translateX(-50%); }
    }

    @media (prefers-reduced-motion: reduce) {
      .track { animation: none; }
    }

    .avatar {
      position: relative;
      flex: 0 0 auto;
      width: 32px;
      height: 32px;
      border-radius: 9999px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      font: 700 11.5px/1 var(--ec-font-display);
      box-shadow: 0 1px 3px rgba(0, 0, 0, .1);
    }

    .avatar .status-emoji {
      position: absolute;
      top: -8px;
      right: -7px;
      width: 20px;
      height: 20px;
      border-radius: 9999px;
      background: #fff;
      box-shadow: 0 1px 2px rgba(0, 0, 0, .12);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      line-height: 1;
    }

    .avatar { animation: bob 2.6s ease-in-out infinite; }
    .avatar:nth-child(2n) { animation-delay: -0.4s; }
    .avatar:nth-child(3n) { animation-delay: -0.9s; animation-duration: 3.1s; }
    .avatar:nth-child(5n) { animation-delay: -1.3s; }
    @keyframes bob {
      0%, 100% { transform: translateY(0); }
      50% { transform: translateY(-2px); }
    }
    @media (prefers-reduced-motion: reduce) {
      .avatar { animation: none; }
    }
  `;

  /**
   * Fictieve medewerker-roster voor de scrollende strip. Bewust géén
   * naam-overlap met onze stub-operator (Sanne) zodat het niet voelt
   * alsof je de uiteindelijke medewerker al ziet langslopen.
   */
  private readonly roster: ReadonlyArray<{ initial: string; color: string; emoji: string }> = [
    { initial: 'M', color: '#4A89DC', emoji: '☕' },
    { initial: 'J', color: '#20AAA2', emoji: '💬' },
    { initial: 'L', color: '#E67E22', emoji: '💻' },
    { initial: 'D', color: '#9B59B6', emoji: '✓' },
    { initial: 'A', color: '#16A085', emoji: '📞' },
    { initial: 'B', color: '#E84393', emoji: '🍪' },
    { initial: 'N', color: '#27AE60', emoji: '✓' },
  ];

  private statusText(): string {
    if (this.elapsedSec < 10) return i18n.searching.statusInitial;
    if (this.elapsedSec < 30) return i18n.searching.statusMid;
    return i18n.searching.statusBusy;
  }

  private formatElapsed(): string {
    const m = Math.floor(this.elapsedSec / 60);
    const s = this.elapsedSec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  }

  override render() {
    // Render de avatar-strip 2× zodat translateX(-50%) seamless loopt.
    const doubled = [...this.roster, ...this.roster];

    return html`
      <div class="banner" part="banner" role="status" aria-live="polite">
        <div class="head">
          <span class="status">${this.statusText()}</span>
          <span class="timer">
            <span class="pulse-dot" aria-hidden="true"></span>
            <span class="timer-text">${this.formatElapsed()}</span>
          </span>
        </div>
        <div class="strip" aria-hidden="true">
          <div class="track">
            ${doubled.map(
              (member) => html`
                <span
                  class="avatar"
                  style="background: ${member.color}"
                >
                  ${member.initial}
                  <span class="status-emoji">${member.emoji}</span>
                </span>
              `,
            )}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'embrace-searching-banner': EmbraceSearchingBanner;
  }
}
